import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Loader2,
  Building2,
  Phone,
  Mail,
  Globe,
  MapPin,
  FileText,
  Hash,
  CreditCard,
} from "lucide-react";

import ImageDropzone from "../../components/common/image-dropzone";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../utils/components/ui/form";
import { Input } from "../../utils/components/ui/input";
import { cn } from "../../utils/cn";
import { organisationServices } from "../../service/companySettingService";
import {
  companyProfileSchema,
  TCompanyProfileFormType,
} from "../../types/schema/companyProfileSchema";
import { toast } from "sonner";

const inputCls =
  "h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF] shadow-none focus-visible:ring-0";

const iconCls =
  "absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A9BB5]";

function CompanySetingsForm() {
  const form = useForm<TCompanyProfileFormType>({
    resolver: zodResolver(companyProfileSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      website: "",
      address: "",
      trn: "",
      stamp: null,
      waterMark: null,
      logo: null,
      bankLogo: null,
      accountNumber: "",
      ibn: "",
    },
  });

  const { data: organisationData } = organisationServices.useGetOrganisation();
  const { mutate: saveOrganisation, isPending } =
    organisationServices.useSaveOrganisation();

  useEffect(() => {
    if (organisationData) {
      form.reset({
        name: organisationData?.name || "",
        phone: organisationData?.phone || "",
        email: organisationData?.email || organisationData?.mail || "",
        website: organisationData?.website || "",
        address: organisationData?.address || "",
        trn: organisationData?.trn || "",
        stamp: organisationData?.stamp || "",
        waterMark: organisationData?.waterMark || "",
        logo: organisationData?.logo || "",
        bankLogo: organisationData?.bankLogo || "",
        accountNumber: organisationData?.accountNumber || "",
        ibn: organisationData?.ibn || "",
      });
    }
  }, [organisationData, form]);

  const isSubmissionDisabled = !form.formState.isValid || isPending;

  const onSubmit = (data: TCompanyProfileFormType) => {
    saveOrganisation(
      {
        name: data.name,
        phone: data.phone,
        email: data.email,
        website: data.website || "",
        address: data.address,
        trn: data.trn,
        accountNumber: data.accountNumber,
        ibn: data.ibn,
      },
      {
        onSuccess: () => {
          toast.success("Company profile updated successfully");
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Company Info card */}
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6">
          <h2 className="text-lg font-semibold text-[#21314A] mb-5">
            Company Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Company Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-[#52627C] mb-1.5 block">
                    Company Name
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Building2 className={iconCls} />
                      <Input
                        {...field}
                        type="text"
                        placeholder="Company Name"
                        onInput={(e) => {
                          e.currentTarget.value = e.currentTarget.value.replace(
                            /\s{2,}/g,
                            " ",
                          );
                        }}
                        className={inputCls}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs text-red-500 mt-1" />
                </FormItem>
              )}
            />

            {/* Phone */}
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-[#52627C] mb-1.5 block">
                    Phone Number
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Phone className={iconCls} />
                      <Input
                        {...field}
                        type="tel"
                        placeholder="Phone Number"
                        onInput={(e) => {
                          e.currentTarget.value = e.currentTarget.value.replace(
                            /[^0-9]/g,
                            "",
                          );
                        }}
                        className={inputCls}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs text-red-500 mt-1" />
                </FormItem>
              )}
            />

            {/* Email */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-[#52627C] mb-1.5 block">
                    Email Address
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Mail className={iconCls} />
                      <Input
                        {...field}
                        type="email"
                        placeholder="Email Address"
                        className={inputCls}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs text-red-500 mt-1" />
                </FormItem>
              )}
            />

            {/* Website */}
            <FormField
              control={form.control}
              name="website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-[#52627C] mb-1.5 block">
                    Website{" "}
                    <span className="text-[#B0BCCE] font-normal">
                      (optional)
                    </span>
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Globe className={iconCls} />
                      <Input
                        {...field}
                        type="url"
                        placeholder="https://example.com"
                        className={inputCls}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs text-red-500 mt-1" />
                </FormItem>
              )}
            />

            {/* Address — full width */}
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel className="text-sm font-medium text-[#52627C] mb-1.5 block">
                    Address
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <MapPin className={iconCls} />
                      <Input
                        {...field}
                        type="text"
                        placeholder="Full address"
                        className={inputCls}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs text-red-500 mt-1" />
                </FormItem>
              )}
            />

            {/* TRN — full width */}
            <FormField
              control={form.control}
              name="trn"
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel className="text-sm font-medium text-[#52627C] mb-1.5 block">
                    TRN{" "}
                    <span className="text-[#B0BCCE] font-normal">
                      (Tax Registration Number)
                    </span>
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <FileText className={iconCls} />
                      <Input
                        {...field}
                        type="text"
                        placeholder="Tax Registration Number"
                        className={inputCls}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs text-red-500 mt-1" />
                </FormItem>
              )}
            />
            {/* Account Number */}
            <FormField
              control={form.control}
              name="accountNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-[#52627C] mb-1.5 block">
                    Account Number{" "}
                    <span className="text-[#B0BCCE] font-normal">
                      (optional)
                    </span>
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Hash className={iconCls} />
                      <Input
                        {...field}
                        type="text"
                        placeholder="Account Number"
                        className={inputCls}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs text-red-500 mt-1" />
                </FormItem>
              )}
            />

            {/* IBN */}
            <FormField
              control={form.control}
              name="ibn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-[#52627C] mb-1.5 block">
                    IBN / IBAN{" "}
                    <span className="text-[#B0BCCE] font-normal">
                      (optional)
                    </span>
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <CreditCard className={iconCls} />
                      <Input
                        {...field}
                        type="text"
                        placeholder="IBN / IBAN Number"
                        className={inputCls}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs text-red-500 mt-1" />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Branding card */}
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6">
          <h2 className="text-lg font-semibold text-[#21314A] mb-1">
            Branding &amp; Documents
          </h2>
          <p className="text-sm text-[#8A9BB5] mb-5">
            Upload your logo, stamp, watermark and bank logo for use on invoices
            and documents.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
            <FormField
              control={form.control}
              name="logo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-[#52627C] mb-1.5 block">
                    Logo
                  </FormLabel>
                  <FormControl>
                    <ImageDropzone
                      uploadKey="LOGO"
                      value={field.value || undefined}
                      onChange={field.onChange}
                      accept={{ "image/*": [".png", ".jpg", ".jpeg"] }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="stamp"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-[#52627C] mb-1.5 block">
                    Stamp
                  </FormLabel>
                  <FormControl>
                    <ImageDropzone
                      uploadKey="STAMP"
                      value={field.value || undefined}
                      onChange={field.onChange}
                      accept={{ "image/*": [".png", ".jpg", ".jpeg"] }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="waterMark"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-[#52627C] mb-1.5 block">
                    Watermark
                  </FormLabel>
                  <FormControl>
                    <ImageDropzone
                      uploadKey="WATER_MARK"
                      value={field.value || undefined}
                      onChange={field.onChange}
                      accept={{ "image/*": [".png", ".jpg", ".jpeg"] }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="bankLogo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-[#52627C] mb-1.5 block">
                    Bank Logo
                  </FormLabel>
                  <FormControl>
                    <ImageDropzone
                      uploadKey="BANK_LOGO"
                      value={field.value || undefined}
                      onChange={field.onChange}
                      accept={{ "image/*": [".png", ".jpg", ".jpeg"] }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={form.formState.isSubmitting}
            className={cn(
              "h-11 rounded-[12px] bg-[#1A5EF9] px-8 text-white font-medium text-sm flex items-center gap-2 transition-colors hover:bg-[#0E4FDE]",
            )}
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {isPending ? "Saving…" : "Save Profile"}
          </button>
        </div>
      </form>
    </Form>
  );
}

export default CompanySetingsForm;
